import { MotionDiv } from '@/components/element/MotionDiv';
import { usePurchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { usePurchaseOrderContractTaskPhases } from '@/hooks/api/0770_purchase_order_contract_task_phases';
import { usePurchaseOrderContractTasksInvalidate } from '@/hooks/api/0780_purchase_order_contract_tasks';
import { usePurchaseOrderContractTaskInvalidate } from '@/hooks/api/0820_purchase_order_contract_task';
import { useMovePurchaseOrderContractTaskPhaseToNext } from '@/hooks/api/0830_move_to_next_phase';
import { useMovePurchaseOrderContractTaskPhaseToPrev } from '@/hooks/api/0831_move_to_prev_phase';
import { PurchaseOrderContractTaskPhase } from '@/hooks/api/models';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPurchaseOrderContractTask } from '@/providers/PurchaseOrderContractTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Fragment, RefObject, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTeamPurchaseOrderPhases } from '@/hooks/api/0260_team_purchase_order_phases';
import { useUpdatePurchaseOrderPhase } from '@/hooks/api/0580_update_purchase_order_phase';
import { ModalMessageConfirm } from '@/components/element/modal/ModalMessageConfirm';

export const TaskProgress = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { purchaseOrderContractTask: task } = useCurrentPurchaseOrderContractTask();
  const { data: phases } = usePurchaseOrderContractTaskPhases();
  const [currentPhase, setCurrentPhase] = useState(task!.purchase_order_contract_task_phase);
  const { data: teamPurchaseOrderPhases } = useTeamPurchaseOrderPhases({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
  });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: task!.purchase_order.id,
  });
  const { invalidate: invalidateTasks } = usePurchaseOrderContractTasksInvalidate(_w_!.identifier);
  const showMessageButtonRef = useRef<HTMLButtonElement>(null);

  const {
    mutate: updatePurchaseOrderPhaseMutate,
    data: purchaseOrder,
    isPending: isUpdatePurchaseOrderPhasePending,
  } = useUpdatePurchaseOrderPhase();

  useEffect(() => {
    setCurrentPhase(task!.purchase_order_contract_task_phase);
    invalidateTasks();
    invalidatePurchaseOrder();
  }, [task]);

  // 確認ダイアログで「変更する」クリック時、PurchaseOrderのフェーズを契約中に変更する
  const handleUpdateOrderPhaseClick = () => {
    updatePurchaseOrderPhaseMutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: task!.purchase_order.id,
      team_purchase_order_phase_id: Number(teamPurchaseOrderPhases!.find((p) => p.seq == 2)!.id),
    });
  };

  useEffect(() => setShowLoading(isUpdatePurchaseOrderPhasePending), [isUpdatePurchaseOrderPhasePending]);

  useEffect(() => {
    if (purchaseOrder) {
      invalidatePurchaseOrder();
    }
  }, [purchaseOrder]);

  if (phases == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">タスク進捗状況</h6>
        </div>
      </div>
      <div className="card-body pt-0">
        <ModalMessageConfirm
          modalId="messageModal"
          message={'契約が承認されました。\nプロジェクトのフェーズを契約中に変更しますか？'}
          messageLink={{
            url: `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${task!.purchase_order.id}`,
            label: `${task!.purchase_order.name}`,
          }}
          showModalButtonRef={showMessageButtonRef}
          okClicked={handleUpdateOrderPhaseClick}
          okButtonLabel={'変更する'}
          cancelButtonLabel={'変更しない'}
        />

        {phases.map((phase, i) => (
          <Fragment key={`poctp-${phase.id}`}>
            {i != 0 && <hr className="horizontal dark" />}
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-sm text-dark font-weight-bold">{phase.label}</div>
              <div className="d-flex justify-content-end align-items-center">
                {phase.id == currentPhase.id && (
                  <img src={task!.user.user_account.icon_file_url} className="avatar avatar-sm rounded-circle" />
                )}
                {phase.id < currentPhase.id && <span className="badge badge-success">OK</span>}
                <MoveToPrevPhaseButton
                  phase={phase}
                  curPhase={currentPhase}
                  phases={phases}
                  showMessageButtonRef={showMessageButtonRef}
                />
                <MoveToNextPhaseButton
                  phase={phase}
                  curPhase={currentPhase}
                  phases={phases}
                  showMessageButtonRef={showMessageButtonRef}
                />
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </MotionDiv>
  );
};

type ButtonProps = {
  phase: PurchaseOrderContractTaskPhase;
  curPhase: PurchaseOrderContractTaskPhase;
  phases: PurchaseOrderContractTaskPhase[];
  showMessageButtonRef: RefObject<HTMLButtonElement>;
};

// 次のフェーズボタン
const MoveToNextPhaseButton = ({ phase, curPhase, phases, showMessageButtonRef }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember, isApprover } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { purchaseOrderContractTask: task } = useCurrentPurchaseOrderContractTask();
  const { mutate, data, isPending, isSuccess, error } = useMovePurchaseOrderContractTaskPhaseToNext();
  const { invalidate: invalidateTask } = usePurchaseOrderContractTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      invalidateTask();

      // NDA以外かつ承認済み以降のフェーズの場合に確認ダイアログを表示
      if (task!.contract_context.id != 2 && data.purchase_order.team_purchase_order_phase.seq < 2) {
        if (
          data.purchase_order_contract_task_phase.name == 'approved' ||
          data.purchase_order_contract_task_phase.name == 'completed'
        ) {
          showMessageButtonRef.current?.click();
        }
      }
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      current_purchase_order_contract_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases.at(-1)!.id) return <></>;

  return (
    <button
      className={`btn btn-icon-only bg-gradient-success ms-1 ${
        phase.name == 'request_approval' && !isApprover ? 'disabled' : ''
      }`}
      onClick={onClick}
    >
      <i className="fas fa-arrow-down"></i>
    </button>
  );
};

// 前のフェーズボタン
const MoveToPrevPhaseButton = ({ phase, curPhase, phases }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember, isApprover } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { purchaseOrderContractTask: task } = useCurrentPurchaseOrderContractTask();
  const { mutate, data, isPending, isSuccess, error } = useMovePurchaseOrderContractTaskPhaseToPrev();
  const { invalidate: invalidateTask } = usePurchaseOrderContractTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) invalidateTask();
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: Number(purchase_order_id),
      task_id: task!.id,
      current_purchase_order_contract_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases[0].id) return <></>;

  return (
    <button className="btn btn-icon-only bg-gradient-warning ms-1" onClick={onClick}>
      <i className="fas fa-arrow-up"></i>
    </button>
  );
};
